// StagedPartsSection — Phase 4B
//
// Renders staged (not-yet-persisted) parts during Create Ticket. Supports
// optional binding to a staged job via tempJobId. The actual insert into
// parts_lines happens AFTER the ticket + ticket_jobs are persisted, with an
// explicit tempJobId → realJobId map (no reliance on insert ordering).

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus, Package2, Trash2, Pencil } from 'lucide-react';

export interface StagedPart {
  tempId: string;
  tempJobId: string | null;
  description: string;
  vendor_name: string | null;
  vendor_part_number: string | null;
  category: string | null;
  quantity: number;
  unit_cost: number | null;
  unit_sell: number | null;
  taxable: boolean;
  core_charge: number;
  notes: string | null;
}

export interface StagedJobOption {
  tempJobId: string;
  name: string;
}

interface Props {
  stagedParts: StagedPart[];
  onChange: (next: StagedPart[]) => void;
  jobOptions: StagedJobOption[];
}

const blank = (): Omit<StagedPart, 'tempId'> => ({
  tempJobId: null,
  description: '',
  vendor_name: null,
  vendor_part_number: null,
  category: null,
  quantity: 1,
  unit_cost: null,
  unit_sell: null,
  taxable: true,
  core_charge: 0,
  notes: null,
});

export function StagedPartsSection({ stagedParts, onChange, jobOptions }: Props) {
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Omit<StagedPart, 'tempId'>>(blank());

  const openAdd = () => {
    setEditingId(null);
    setDraft(blank());
    setOpen(true);
  };

  const openEdit = (p: StagedPart) => {
    setEditingId(p.tempId);
    const { tempId: _id, ...rest } = p;
    setDraft(rest);
    setOpen(true);
  };

  const remove = (tempId: string) => {
    onChange(stagedParts.filter((p) => p.tempId !== tempId));
  };

  const save = () => {
    if (!draft.description.trim()) return;
    if (editingId) {
      onChange(
        stagedParts.map((p) =>
          p.tempId === editingId ? { ...draft, tempId: editingId } : p,
        ),
      );
    } else {
      onChange([
        ...stagedParts,
        { ...draft, tempId: crypto.randomUUID() },
      ]);
    }
    setOpen(false);
  };

  const jobNameByTemp = new Map(jobOptions.map((j) => [j.tempJobId, j.name]));

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Package2 className="h-5 w-5" />
            Parts ({stagedParts.length})
          </CardTitle>
          <CardDescription>
            Optional. Parts can target the whole ticket or a specific job above.
          </CardDescription>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={openAdd}>
          <Plus className="h-4 w-4 mr-1" />
          Add Part
        </Button>
      </CardHeader>
      <CardContent>
        {stagedParts.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            No parts staged yet.
          </p>
        ) : (
          <div className="space-y-2">
            {stagedParts.map((p) => {
              const target = p.tempJobId
                ? jobNameByTemp.get(p.tempJobId) ?? 'Linked job'
                : 'Whole ticket';
              return (
                <div
                  key={p.tempId}
                  className="flex items-center justify-between gap-3 rounded-md border bg-muted/20 p-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm truncate">{p.description}</span>
                      <Badge variant="secondary" className="text-[11px]">
                        {target}
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Qty {p.quantity}
                      {p.unit_cost != null && ` · cost $${p.unit_cost.toFixed(2)}`}
                      {p.unit_sell != null && ` · sell $${p.unit_sell.toFixed(2)}`}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => openEdit(p)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => remove(p.tempId)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[560px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Part' : 'Add Part'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="sp-desc">Description *</Label>
              <Input
                id="sp-desc"
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                placeholder="Front brake pads, premium ceramic"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="sp-vendor">Vendor</Label>
                <Input
                  id="sp-vendor"
                  value={draft.vendor_name ?? ''}
                  onChange={(e) =>
                    setDraft({ ...draft, vendor_name: e.target.value || null })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="sp-vpn">Vendor Part #</Label>
                <Input
                  id="sp-vpn"
                  value={draft.vendor_part_number ?? ''}
                  onChange={(e) =>
                    setDraft({ ...draft, vendor_part_number: e.target.value || null })
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="sp-cat">Category</Label>
                <Input
                  id="sp-cat"
                  value={draft.category ?? ''}
                  onChange={(e) =>
                    setDraft({ ...draft, category: e.target.value || null })
                  }
                  placeholder="brakes"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="sp-qty">Qty</Label>
                <Input
                  id="sp-qty"
                  type="number"
                  min="0"
                  step="0.5"
                  value={draft.quantity}
                  onChange={(e) =>
                    setDraft({ ...draft, quantity: Number(e.target.value || 1) })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="sp-core">Core charge</Label>
                <Input
                  id="sp-core"
                  type="number"
                  min="0"
                  step="0.01"
                  value={draft.core_charge}
                  onChange={(e) =>
                    setDraft({ ...draft, core_charge: Number(e.target.value || 0) })
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="sp-cost">Unit cost</Label>
                <Input
                  id="sp-cost"
                  type="number"
                  min="0"
                  step="0.01"
                  value={draft.unit_cost ?? ''}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      unit_cost: e.target.value === '' ? null : Number(e.target.value),
                    })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="sp-sell">Unit sell</Label>
                <Input
                  id="sp-sell"
                  type="number"
                  min="0"
                  step="0.01"
                  value={draft.unit_sell ?? ''}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      unit_sell: e.target.value === '' ? null : Number(e.target.value),
                    })
                  }
                  placeholder="leave blank for now"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Assign to</Label>
              <Select
                value={draft.tempJobId ?? '__ticket__'}
                onValueChange={(v) =>
                  setDraft({ ...draft, tempJobId: v === '__ticket__' ? null : v })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__ticket__">Whole ticket</SelectItem>
                  {jobOptions.map((j) => (
                    <SelectItem key={j.tempJobId} value={j.tempJobId}>
                      {j.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-3">
              <Switch
                id="sp-tax"
                checked={draft.taxable}
                onCheckedChange={(v) => setDraft({ ...draft, taxable: !!v })}
              />
              <Label htmlFor="sp-tax">Taxable</Label>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="sp-notes">Notes</Label>
              <Textarea
                id="sp-notes"
                value={draft.notes ?? ''}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value || null })}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={save} disabled={!draft.description.trim()}>
              {editingId ? 'Save' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
